import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Stack,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  Badge,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isTokenExpired } from '../utils/authUtils';
import secureStorage from '../utils/secureStorage';
import { RoleNames } from '../constants/roles';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CookieIcon from '@mui/icons-material/Cookie';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArticleIcon from '@mui/icons-material/Article';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import NotificationBell from './NotificationBell';
import { openCookieSettings } from '../utils/cookieConsent';

const menuItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'Properties', icon: <HomeWorkIcon />, path: '/properties' },
  { text: 'Market Analysis', icon: <TrendingUpIcon />, path: '/market' },
  { text: 'About', icon: <InfoIcon />, path: '/about' },
  { text: 'FAQ', icon: <HelpIcon />, path: '/faq' },
  { text: 'Contact', icon: <ContactSupportIcon />, path: '/contact' },
  { text: 'Cookie Settings', icon: <CookieIcon />, action: 'cookie-settings' },
];

const Header = () => {
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const isLoggedIn = Boolean(state?.isAuthenticated && state?.user);

  useEffect(() => {
    const checkToken = async () => {
      const token = secureStorage.getToken();
      if (state?.isAuthenticated && token && token !== 'cookie-auth' && isTokenExpired(token)) {
        await logout();
        navigate('/login');
      }
    };
    checkToken();
  }, [state?.isAuthenticated, logout, navigate]);

  useEffect(() => {
    // Reset image error state when current user/avatar changes.
    setAvatarLoadFailed(false);
  }, [
    state?.user?.id,
    state?.user?.avatar_url,
    state?.user?.avatarUrl,
    state?.user?.picture,
    state?.user?.profile_image,
    state?.user?.profileImage,
  ]);

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Update avatar display
  const getInitials = () => {
    if (!state.user) return '';
    return `${state.user.first_name?.[0]?.toUpperCase() || ''}${state.user.last_name?.[0]?.toUpperCase() || ''}`;
  };

  const getAvatarUrl = () => {
    const avatarUrl =
      state?.user?.avatar_url ||
      state?.user?.avatarUrl ||
      state?.user?.picture ||
      state?.user?.profile_image ||
      state?.user?.profileImage ||
      null;

    if (typeof avatarUrl !== 'string') {
      return null;
    }
    const normalized = avatarUrl.trim();
    return normalized || null;
  };

  const avatarUrl = avatarLoadFailed ? null : getAvatarUrl();

  return (
    <>
      <AppBar 
        position="fixed"
        elevation={0} 
        data-testid="auth-state"
        data-authenticated={state.isAuthenticated}
        data-user={state.user?.email || 'none'}
        sx={{ 
          // background: theme.modern.backgrounds.gradient,
          borderBottom: `1px solid ${theme.modern.colors.modern.slate[300]}`,
          // backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.99)',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 15,
          borderBottomRightRadius: 15,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMenuOpen(true)}
            sx={{ mr: 2, color: '#1a2b4c' }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              ...theme.modern.utils.gradientText('primary'),
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              mr: 'auto' // Push user menu to the right
            }}
            onClick={() => navigate('/')}
          >
            {/* <HomeIcon sx={{ color: theme.modern.colors.modern.mainColor[500] }} /> */}
            KeyTa
          </Typography>
          
          <Stack direction="row" spacing={1}>
            {isLoggedIn ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NotificationBell />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      padding: '6px 4px',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    onClick={handleUserMenuClick}
                  >
                    <Avatar
                      src={avatarUrl || undefined}
                      alt={`${state.user?.first_name || ''} ${state.user?.last_name || ''}`.trim() || 'User avatar'}
                      onError={() => setAvatarLoadFailed(true)}
                      sx={{
                        width: 32,
                        height: 32,
                        background: avatarUrl ? 'transparent' : theme.modern.colors.gradient.primary,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getInitials()}
                    </Avatar>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: '#1a2b4c',
                          lineHeight: 1.2
                        }}
                      >
                        {state.user?.first_name} {state.user?.last_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          fontSize: '0.75rem'
                        }}
                      >
                        {/* {state.user?.primary_role?.name || (state.user?.roles?.length > 0 ? state.user.roles[0].name : 'User')} */}
                        {(() => {
                        const roleName = state.user?.primary_role?.display_name || (state.user?.roles?.length > 0 ? state.user.roles[0].display_name : '');
                        return roleName.toLowerCase() !== 'client' && (
                          <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontSize: '0.75rem'
                          }}
                          >
                          {roleName}
                          </Typography>
                        );
                        })()}
                      </Typography>
                    </Box>
                  </Box>

                  <Menu
                    id="user-menu"
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
                      }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {state.user?.first_name} {state.user?.last_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {state.user?.email}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        navigate('/profile');
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20, color: '#64748b' }} />
                      <Typography variant="body2">Profile</Typography>
                    </MenuItem>
                    
                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        navigate('/dashboard');
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 20, color: '#64748b' }} />
                      <Typography variant="body2">Dashboard</Typography>
                    </MenuItem>
                    
                    {/* Super Admin Menu Items - Show for super admin users only */}
                    {(state.user?.roles?.some(role => role.name === RoleNames.SUPER_ADMIN) || 
                      state.user?.primary_role?.name === RoleNames.SUPER_ADMIN) && [
                        <Divider key="super-admin-divider-1" />,
                        <MenuItem 
                          key="rbac-management"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/rbac-management');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <SecurityIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">RBAC Management</Typography>
                        </MenuItem>,

                        <MenuItem
                          key="users-management"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/users-management');
                          }}
                          sx={{
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">Users And Visitors</Typography>
                        </MenuItem>,

                        <MenuItem
                          key="users-properties"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/users-properties');
                          }}
                          sx={{
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <HomeWorkIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">User Properties</Typography>
                        </MenuItem>,

                        <Divider key="super-admin-divider-2" />,
                        <MenuItem 
                          key="prompts"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/prompts');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">Prompts</Typography>
                        </MenuItem>,
                        <MenuItem 
                          key="history"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/history');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <ArticleIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">Conversations History</Typography>
                        </MenuItem>,                      
                        <Divider key="super-admin-divider-3" />,
                        <MenuItem 
                          key="metrics"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/metrics');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">System Metrics</Typography>
                        </MenuItem>,
                        <MenuItem 
                          key="logs"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/logs');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <ArticleIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">System Logs</Typography>
                        </MenuItem>,
                        <Divider key="super-admin-divider-4" />,
                        <MenuItem 
                          key="admin-settings"
                          onClick={() => {
                            handleUserMenuClose();
                            navigate('/admin/settings');
                          }}
                          sx={{ 
                            py: 1,
                            gap: 1.5
                          }}
                        >
                          <SettingsIcon sx={{ fontSize: 20, color: '#64748b' }} />
                          <Typography variant="body2">System Settings</Typography>
                        </MenuItem>
                      ]}

                    <Divider />

                    <MenuItem 
                      onClick={() => {
                        handleUserMenuClose();
                        handleLogout();
                      }}
                      sx={{ 
                        py: 1,
                        gap: 1.5,
                        color: 'error.main'
                      }}
                    >
                      <LogoutIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body2">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            ) : (
              <>
                <Button 
                  variant="text" 
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(59, 123, 134, 0.04)' }
                  }}
                  onClick={() => navigate('/login')}
                >
                  Log In
                </Button>
                <Button 
                  variant="gradient" 
                  onClick={() => navigate('/signup')}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <Box sx={{ width: 280, pt: 2, pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                ...theme.modern.typography.modernHeading,
                fontWeight: 700 
              }}
            >
              Menu
            </Typography>
            <IconButton onClick={() => setMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ flexGrow: 1 }}>
            {menuItems.map((item) => (
              <ListItem 
                button 
                key={item.text}
                onClick={() => {
                  if (item.action === 'cookie-settings') {
                    openCookieSettings();
                  } else {
                    navigate(item.path);
                  }
                  setMenuOpen(false);
                }}
              >
                <ListItemIcon sx={{ color: theme.modern.colors.modern.slate[500], minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>

          {!isLoggedIn ? (
            <>
              {/* <Divider /> */}
              <Box sx={{ p: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    ...theme.modern.typography.modernSubtext 
                  }}
                >
                  {/* Account */}
                </Typography>
                <Button
                  fullWidth
                  variant="gradient"
                  sx={{ 
                    mb: 1.5,
                    // borderColor: 'rgba(0, 0, 0, 0.1)',
                    // color: '#1a2b4c',
                  }}
                  onClick={() => {
                    navigate('/login');
                    setMenuOpen(false);
                  }}
                >
                  Log In
                </Button>
                <Button
                  fullWidth
                  variant="gradient"
                  onClick={() => {
                    navigate('/signup');
                    setMenuOpen(false);
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </>
          ) : (
            <>
              {/* <Divider /> */}
              <Box sx={{ px: 3, pb: 0}}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0, 
                    ...theme.modern.typography.modernSubtext 
                  }}
                >
                </Typography>
                 <Button
                    variant="gradient"
                    fullWidth
                    onClick={() => navigate('/profile')}
                    sx={{
                      my: 0,
                    }}
                  >
                    My Profile
                  </Button>
                  <Button
                    variant="dark"
                    fullWidth
                    color="error"
                    onClick={handleLogout}
                    sx={{ my: 1 }}
                  >
                    Logout
                  </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;